/**
 * Which drums of a launched product can be transformed, and how much of each.
 *
 * ── what this replaces ──────────────────────────────────────────────────────
 *
 * The wizard used to see only what Project Token State had handed it in
 * `location.state.rows`, and offered two modes:
 *
 *   TOTAL    convert the whole product in place — and it did NOT look at the
 *            drum selection at all. A CYYF 5x1.5 with five 15 000 m drums
 *            ticked, left on Total, converted the entire 30 000 m lot:
 *            "Degeaba selectez eu bobine la 15000, ca daca la how much las
 *            TOTAL, imi pune tot lotul la 30000." It also rewrote tasks that
 *            were already DONE, which is the complaint that killed it:
 *            "am patit in trecut sa imi converteasca si produse in faza de
 *            done ca executie, cand am selectat din greseala TOTAL."
 *   PARTIAL  one fork length applied equally to every ticked drum.
 *
 * TOTAL is gone from the wizard (2026-10-30, by decision). Every transform is
 * now a selection of drums with a per-drum figure, so nothing can be touched
 * that was not ticked.
 *
 * ── the model ───────────────────────────────────────────────────────────────
 *
 * One phase at a time. The toggle lists every phase so the planner can see what
 * the product holds, but ticking locks to the phase of the first tick — because
 * a rope CONSUMES the conductors behind it, so forking at the rope and at
 * conductor_isolation in one go would move the same metres twice.
 *
 * Per-drum amounts are free, with a warning rather than a veto when the colours
 * of one cable disagree: 1 000 m of BLUE and 500 m of BROWN can only build
 * 500 m of cable, and the extra BLUE sits in the new product with nothing to
 * pair with. The planner is told; the planner decides.
 */

// The SAME rule the endpoint applies, duplicated byte-for-byte from
// redis_dev/helpers/forkFreeLength.js — the two services cannot import from
// each other. A parity test holds them identical. Without it the screen says
// "4,561 m free" and the endpoint says "skipped", which is exactly the
// contradiction reported on 2026-10-30.
// Spelled with the extension so the mirror can resolve it — Node cannot read
// an extensionless relative import and the mirror refuses to guess.
import { forkFreeLength } from './forkFreeLength.js';

const num = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const U = v => String(v ?? '').trim().toUpperCase();

/** Phases whose drums are parallel cores of ONE cable, not a sequence. */
export const PARALLEL_PHASES = new Set(['conductor_isolation', 'unwinding']);

const DONE = new Set(['DONE', 'COMPLETED', 'EXECUTED', 'FINISHED']);

/**
 * What state a drum is in, for the one question this screen asks: can metres
 * leave it, and by which backend path?
 *
 *   produced   it physically exists and has metres left → `fork-product`
 *   pending    queued, nothing made yet → `fork-product-merge`
 *   spent      produced but fully consumed downstream → nothing to move
 *   empty      a husk: no plan, no produce (usually already forked away)
 */
export function drumKind(d) {
  const produced = num(d?.producedLength);
  const planned = num(d?.plannedLength);
  const available = num(d?.availableLength);

  if (produced > 0) return available > 0 ? 'produced' : 'spent';
  if (DONE.has(U(d?.status)) && planned <= 0) return 'empty';
  if (planned > 0) return 'pending';
  return 'empty';
}

/** The most that can leave this drum. 0 means it cannot be transformed. */
export function maxTake(d) {
  const kind = drumKind(d);
  if (kind === 'produced') return num(d?.availableLength);
  // A queued drum has nothing on it yet, so what can move is its PLAN.
  if (kind === 'pending') return num(d?.plannedLength);
  return 0;
}

/** Can this drum take part at all? */
export const isTransformable = d => maxTake(d) > 0;

/**
 * Every drum of a job, across every phase, in phase order.
 *
 * Reads the raw job document — `/api/redis/jobs/:jobId` returns it whole, so
 * the "bring all phases" toggle needs no endpoint of its own. `phaseOrder` is
 * the job's own `phasePlan`, which is the order the planner reads on every
 * other screen; anything not in it is appended so a bolted-on coiling phase
 * cannot disappear.
 */
export function drumsOfJob(job) {
  const tokens = job?.tokens && typeof job.tokens === 'object' ? job.tokens : {};
  const tasks = job?.tasks && typeof job.tasks === 'object' ? job.tasks : {};

  const planNames = Array.isArray(job?.phasePlan)
    ? job.phasePlan.map(p => p?.name || p?.phase).filter(Boolean)
    : [];
  const rank = new Map(planNames.map((n, i) => [n, i]));

  // Which machine made (or is queued to make) each drum, for the card. A token
  // names no machine; the task that lists it in `outputs` does.
  const machineOf = new Map();
  for (const t of Object.values(tasks)) {
    if (!Array.isArray(t?.outputs)) continue;
    for (const out of t.outputs) {
      if (!machineOf.has(out)) {
        machineOf.set(out, { machineId: t.machineId || null, status: U(t.status) });
      }
    }
  }

  const out = [];
  for (const [tokenId, tok] of Object.entries(tokens)) {
    if (!tok) continue;
    const d = {
      tokenId,
      phase: tok.phase || 'unknown',
      color: tok.color ?? null,
      wireDiameter: tok.wireDiameter ?? null,
      plannedLength: num(tok.plannedLength),
      producedLength: num(tok.producedLength),
      availableLength: num(tok.availableLength),
      consumedLength: num(tok.consumedLength),
      reservedLength: num(tok.reservedLength),
      status: U(tok.status) || 'PENDING',
      drum_material: tok.drum_material ?? null,
      drum_diameter: tok.drum_diameter ?? null,
      machineId: machineOf.get(tokenId)?.machineId ?? null,
      machineStatus: machineOf.get(tokenId)?.status ?? null
    };
    d.kind = drumKind(d);

    // WHAT CAN ACTUALLY LEAVE IT, from the shared rule — not just
    // `availableLength`. A drum whose child is planned but not yet produced has
    // fewer free metres than it appears to, and `reservedLength` does not
    // always say so.
    const fl = forkFreeLength(job, tokenId);
    d.free = fl.free;
    d.heldLength = fl.held;
    d.pendingClaim = fl.pending;
    d.pendingChildren = fl.children;
    // The children ON A MACHINE now. A rope turning on skip2250 is drawing from
    // specific cores, and the planner has to know WHICH — flagged 2026-10-30:
    // "e foarte important din ce tokeni de conductor isolation isi trage sa faca
    // ropeul activ."
    d.runningChildren = fl.running || [];
    // `spent` and `empty` drums give nothing whatever the arithmetic says.
    d.maxTake = d.kind === 'produced' || d.kind === 'pending' ? fl.free : 0;
    out.push(d);
  }

  const r = p => (rank.has(p) ? rank.get(p) : 900 + p.length);
  return out.sort(
    (a, b) =>
      r(a.phase) - r(b.phase) ||
      String(a.color || '').localeCompare(String(b.color || '')) ||
      String(a.tokenId).localeCompare(String(b.tokenId))
  );
}

/** Group drums by phase, keeping the job's own phase order. */
export function byPhase(drums) {
  const groups = [];
  const seen = new Map();
  for (const d of Array.isArray(drums) ? drums : []) {
    if (!seen.has(d.phase)) {
      const g = { phase: d.phase, drums: [] };
      seen.set(d.phase, g);
      groups.push(g);
    }
    seen.get(d.phase).drums.push(d);
  }
  return groups;
}

/**
 * The phase a selection is locked to — the phase of the first ticked drum.
 *
 * `null` for an empty selection, which is what unlocks the list again.
 */
export function lockedPhase(drums, selectedIds) {
  const ids = selectedIds instanceof Set ? selectedIds : new Set(selectedIds || []);
  for (const d of Array.isArray(drums) ? drums : []) {
    if (ids.has(d.tokenId)) return d.phase;
  }
  return null;
}

/**
 * Why a drum cannot be ticked right now — a sentence, or null if it can.
 *
 * Said per drum rather than by disabling silently: "it is greyed out and I do
 * not know why" is the state this screen was already in.
 */
export function blockedReason(d, { locked = null, kind = null } = {}) {
  const k = kind || drumKind(d);
  if (k === 'spent') {
    return 'Fully consumed downstream — there are no metres left to move.';
  }
  if (k === 'empty') {
    return 'Nothing on this drum and nothing planned for it.';
  }
  if (locked && d.phase !== locked) {
    return (
      `One phase at a time. This selection is on ${locked.replaceAll('_', ' ')} — ` +
      `a later phase consumes the drums of an earlier one, so moving both would ` +
      `move the same metres twice. Untick the others to switch phase.`
    );
  }
  return null;
}

/**
 * The plan: what leaves each drum and what stays.
 *
 * `takes` is `{ [tokenId]: metres }` straight off the inputs. Everything else
 * here is derived, so the preview and the submit cannot disagree — they are
 * handed the same object.
 */
export function planTransform({ drums = [], selectedIds, takes = {} } = {}) {
  const ids = selectedIds instanceof Set ? selectedIds : new Set(selectedIds || []);
  const picked = drums.filter(d => ids.has(d.tokenId));
  const phase = picked[0]?.phase ?? null;

  const rows = picked.map(d => {
    const want = num(takes[d.tokenId]);
    const take = Math.max(0, Math.min(want, d.maxTake));
    return {
      ...d,
      take,
      keep: Math.max(0, d.maxTake - take),
      // The planner typed more than the drum holds. Clamped rather than
      // refused, and said so — a silent clamp is how a 15 000 m entry becomes
      // a 1 236 m transform without anybody noticing.
      clamped: want > d.maxTake + 1e-6 ? want : null
    };
  });

  // Per colour, because that is the unit a cable is built from: two drums of
  // one colour are sequential and sum, while different colours are parallel and
  // each has to supply the whole length.
  const byColour = new Map();
  for (const r of rows) {
    const key = r.color ? U(r.color) : r.wireDiameter ? `Ø${r.wireDiameter}` : '-';
    byColour.set(key, num(byColour.get(key)) + r.take);
  }
  const colourTotals = [...byColour.entries()].map(([color, take]) => ({ color, take }));

  const totalTake = rows.reduce((s, r) => s + r.take, 0);
  const kinds = new Set(rows.map(r => r.kind));

  // How long the new product can actually be. For parallel cores it is the
  // SHORTEST colour — a cable is as long as its shortest core, and the surplus
  // on the others is stranded.
  const parallel = PARALLEL_PHASES.has(phase);
  const buildable = !rows.length
    ? 0
    : parallel && colourTotals.length
      ? Math.min(...colourTotals.map(c => c.take))
      : totalTake;

  const surplus = parallel
    ? colourTotals
        .filter(c => c.take > buildable + 1e-6)
        .map(c => ({ color: c.color, metres: c.take - buildable }))
    : [];

  return {
    phase,
    rows,
    colourTotals,
    totalTake: Math.round(totalTake * 1000) / 1000,
    buildable: Math.round(buildable * 1000) / 1000,
    surplus,
    parallel,
    // Which backend path this selection takes. A selection that mixes the two
    // has no single path, which is its own refusal below.
    path:
      kinds.size !== 1
        ? null
        : kinds.has('pending')
          ? 'merge'
          : kinds.has('produced')
            ? 'fork'
            : null,
    mixedKinds: kinds.size > 1,
    mixedPhase: picked.some(d => d.phase !== phase)
  };
}

/**
 * Everything wrong with a plan, worst first.
 *
 * `blocking: true` stops the submit. Anything else is said and allowed —
 * including unequal colours, which is a real choice a planner may want to make
 * ("complet liber, doar avertisment").
 */
export function problemsOf(plan, { sourceLength = 0 } = {}) {
  const out = [];
  if (!plan || !plan.rows.length) {
    out.push({ blocking: true, text: 'Tick at least one drum to transform.' });
    return out;
  }

  if (plan.mixedPhase) {
    out.push({
      blocking: true,
      text: 'The selection spans more than one phase. One phase at a time.'
    });
  }

  if (plan.mixedKinds) {
    out.push({
      blocking: true,
      text:
        'The selection mixes drums that are already made with drums still in the ' +
        'queue. They take different paths — a produced drum is cut, a queued one ' +
        'is split into a merged pair — so they cannot go in one transform.'
    });
  }

  if (plan.totalTake <= 0) {
    out.push({ blocking: true, text: 'Nothing is being moved: every figure is 0.' });
  }

  for (const r of plan.rows.filter(r => r.clamped != null)) {
    out.push({
      blocking: false,
      text:
        `${r.tokenId}: ${fmt(r.clamped)} m asked for, but the drum only has ` +
        `${fmt(r.maxTake)} m — reduced to that.`
    });
  }

  // The headline warning, and the reason per-drum figures are allowed to differ.
  if (plan.surplus.length) {
    out.push({
      blocking: false,
      text:
        `The colours do not match: the new product can only be ${fmt(plan.buildable)} m ` +
        `long, and ${plan.surplus
          .map(s => `${fmt(s.metres)} m of ${s.color}`)
          .join(', ')} will sit in it with no core to pair with.`
    });
  }

  if (sourceLength > 0 && plan.buildable > sourceLength + 1e-6) {
    out.push({
      blocking: false,
      text: `${fmt(plan.buildable)} m is more than the source lot's ${fmt(sourceLength)} m.`
    });
  }

  return out.sort((a, b) => Number(b.blocking) - Number(a.blocking));
}

const fmt = n => Math.round(num(n)).toLocaleString('ro-RO');

// ───────────────────────────────────────────────────────────────────────────
// WHAT THE FORK WILL REFUSE, said before the planner presses the button.
// ───────────────────────────────────────────────────────────────────────────
//
// `forkProduct` drops a link rather than failing the whole request, and the
// only sign used to be a warning in the response — "token K-a8eb254f: fork
// 15000 > effective 0" — read after the transform had already happened. These
// two functions run the SAME rules here, while a drum can still be unticked.
//
// Mirrored from redis_dev/controller/jobs.js `forkProduct`:
//
//   capacity    = max(availableLength, plannedLength)
//   unavailable = max(reservedLength, consumedLength)      ← max, never the sum
//   effective   = max(0, capacity - unavailable)
//
//   · amount > effective            → the drum is spoken for
//   · any task lists it in `inputs` → consumed downstream, dropped outright
//
// `unavailable` takes the GREATER of reserved and consumed, not their sum: a
// drum whose downstream was planned (reserved) and then produced (consumed)
// would otherwise be counted against itself twice.

/** The metres of a drum a fork may actually take. Mirrors `tokenEffective`. */
export function tokenEffective(tok) {
  if (!tok) return 0;
  const capacity = Math.max(num(tok.availableLength), num(tok.plannedLength));
  const unavailable = Math.max(num(tok.reservedLength), num(tok.consumedLength));
  return Math.max(0, capacity - unavailable);
}

/**
 * Which of the ticked drums the fork will leave behind, and why.
 *
 * @param job      the source job document
 * @param tokenIds the ticked drums
 * @param amount   one figure for all of them (legacy callers), or
 * @param takes    `{ [tokenId]: metres }` when each drum has its own figure
 * @returns `[{ tokenId, colour, reason, phases, forkedTo, committed, effective }]`
 */
export function transformSkips({ job, tokenIds = [], amount = 0, takes = null } = {}) {
  const tokens = job?.tokens && typeof job.tokens === 'object' ? job.tokens : {};
  const tasks = job?.tasks && typeof job.tasks === 'object' ? job.tasks : {};
  const out = [];

  for (const id of Array.isArray(tokenIds) ? tokenIds : []) {
    const tok = tokens[String(id)];
    if (!tok) continue;

    const want = takes ? num(takes[id]) : num(amount);

    // The SAME arithmetic the endpoint runs. It used to be `tokenEffective`
    // plus a blanket refusal on the existence of any downstream consumer, which
    // made a drum that had given part of itself to a rope unforkable for ever.
    const fl = forkFreeLength(job, String(id));
    const effective = fl.free;
    const committed = Math.max(0, fl.held - fl.free);

    // Which phases have spoken for it — named, because "spoken for" is not
    // actionable and "spoken for by the rope" is. Only the children that have
    // NOT been made yet: a finished rope's draw is already in `consumedLength`
    // and is not a reason to refuse the remainder.
    const phases = [
      ...new Set(fl.children.map(c => c.phase).filter(Boolean))
    ];

    const forkedTo = (Array.isArray(tok.forkedTo) ? tok.forkedTo : []).filter(
      f => f && f.jobId
    );

    const base = {
      tokenId: String(id),
      colour: tok.color ?? null,
      phases,
      forkedTo,
      committed,
      effective
    };

    // Order matters: the most specific explanation wins, so a drum that LEFT is
    // not reported as merely short.
    if (forkedTo.length && effective <= 0) {
      out.push({ ...base, reason: 'forked' });
      continue;
    }
    if (want > effective + 1e-6) {
      // Three different pieces of advice, so three reasons. A drum feeding a
      // machine that is turning has to be left alone until it stops; a planned
      // child can be un-planned; a simply short drum needs a smaller figure.
      out.push({
        ...base,
        reason: (fl.running || []).length
          ? 'running'
          : fl.pending > 0
            ? 'planned'
            : 'short',
        running: fl.running || []
      });
    }
  }

  return out;
}

/** One sentence per skipped drum, for the acknowledgement list. */
export function skipReasonText(skip, { sourceName = 'the source' } = {}) {
  if (!skip) return '';
  const where = skip.phases.length
    ? skip.phases.join(' + ').replaceAll('_', ' ')
    : 'a later phase';

  switch (skip.reason) {
    case 'forked':
      return (
        `already forked to ${skip.forkedTo
          .map(f => f.productLabel || f.jobId)
          .join(', ')} — nothing left on it to move.`
      );
    case 'consumed':
      return `already consumed by ${where} in ${sourceName} — those metres are inside another drum now.`;
    case 'running':
      return (
        `${(skip.running || [])
          .map(
            c =>
              `${String(c.phase || 'a later phase').replaceAll('_', ' ')} is RUNNING on ` +
              `${String(c.machineId || '?').toUpperCase()} and still needs ${fmt(c.metres)} m`
          )
          .join('; ')} — only ${fmt(skip.effective)} m are free until it stops.`
      );
    case 'planned':
      return `${fmt(skip.committed)} m already planned into ${where}; only ${fmt(skip.effective)} m are free.`;
    default:
      return `only ${fmt(skip.effective)} m free (${fmt(skip.committed)} m spoken for).`;
  }
}


export default {
  PARALLEL_PHASES,
  tokenEffective,
  transformSkips,
  skipReasonText,
  drumKind,
  maxTake,
  isTransformable,
  drumsOfJob,
  byPhase,
  lockedPhase,
  blockedReason,
  planTransform,
  problemsOf
};
